import React, { useContext, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  CBadge, CButton, CCloseButton, CContainer, CDropdown, CDropdownDivider,
  CDropdownHeader, CDropdownItem, CDropdownMenu, CDropdownToggle, CFooter,
  CHeader, CHeaderBrand, CHeaderNav, CHeaderToggler, CNavItem, CSidebar,
  CSidebarBrand, CSidebarHeader, CSidebarNav, useColorModes,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilAccountLogout, cilContrast, cilMenu, cilMoon, cilSun, cilUser,
} from '@coreui/icons'

import { api } from './api/client'
import { AuthContext } from './App'
import { useVersionCheck } from './utils/useVersionCheck'

const ROLE_BADGE_COLOR: Record<string, string> = {
  viewer: 'secondary',
  contributor: 'info',
  editor: 'warning',
  admin: 'danger',
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth, clear } = useContext(AuthContext)
  const navigate = useNavigate()
  const { colorMode, setColorMode } = useColorModes('costarica-theme')
  const { version, updateAvailable, reload } = useVersionCheck()
  const [sidebarShow, setSidebarShow] = useState(true)

  const onLogout = async () => {
    try { await api.post('/v1/auth/logout') } catch {}
    clear()
    navigate('/login', { replace: true })
  }

  const onLogoutAll = async () => {
    try { await api.post('/v1/auth/logout-all') } catch {}
    clear()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <CSidebar
        className="border-end"
        colorScheme="dark"
        position="fixed"
        visible={sidebarShow}
        onVisibleChange={setSidebarShow}
      >
        <CSidebarHeader className="border-bottom">
          <CSidebarBrand>
            <NavLink
              to="/"
              className="d-flex align-items-center gap-2 text-decoration-none text-white"
            >
              <span style={{ fontSize: 26, lineHeight: 1 }}>🌴</span>
              <span style={{ fontWeight: 600 }}>Costa Rica 2027</span>
            </NavLink>
          </CSidebarBrand>
          <CCloseButton
            className="d-lg-none"
            dark
            onClick={() => setSidebarShow(false)}
          />
        </CSidebarHeader>
        <CSidebarNav>
          <NavItemLink to="/" label="Dashboard" />
          <NavItemLink to="/calendar" label="📅 Calendar" />
          <NavItemLink to="/people" label="👥 People" />
          <NavItemLink to="/flights" label="✈️ Flights" />
          <NavItemLink to="/stays" label="🏨 Stays" />
          <NavItemLink to="/transport" label="🚗 Transport" />
          <NavItemLink to="/activities" label="🗓️ Booked Activities" />
          <NavItemLink to="/ideas" label="💡 Ideas" />
          <NavItemLink to="/tasks" label="📋 Tasks" />
          <NavItemLink to="/ask" label="🤖 Ask Arthur" />
          <NavItemLink to="/money" label="💰 Money" />
          {auth.user?.role === 'admin' && <NavItemLink to="/admin" label="⚙️ Admin" />}
        </CSidebarNav>
      </CSidebar>

      <div className="wrapper d-flex flex-column min-vh-100">
        <CHeader className="px-4 border-bottom">
          <CHeaderToggler
            onClick={() => setSidebarShow(!sidebarShow)}
            style={{ marginInlineStart: '-14px' }}
          >
            <CIcon icon={cilMenu} size="lg" />
          </CHeaderToggler>
          <CHeaderBrand className="mx-auto d-md-none">🌴 Costa Rica 2027</CHeaderBrand>
          <CHeaderNav className="ms-auto">
            {/* Theme switcher */}
            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false}>
                {colorMode === 'dark' ? (
                  <CIcon icon={cilMoon} size="lg" />
                ) : colorMode === 'auto' ? (
                  <CIcon icon={cilContrast} size="lg" />
                ) : (
                  <CIcon icon={cilSun} size="lg" />
                )}
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem
                  active={colorMode === 'light'} as="button" type="button"
                  onClick={() => setColorMode('light')}
                  className="d-flex align-items-center"
                >
                  <CIcon className="me-2" icon={cilSun} size="lg" /> Light
                </CDropdownItem>
                <CDropdownItem
                  active={colorMode === 'dark'} as="button" type="button"
                  onClick={() => setColorMode('dark')}
                  className="d-flex align-items-center"
                >
                  <CIcon className="me-2" icon={cilMoon} size="lg" /> Dark
                </CDropdownItem>
                <CDropdownItem
                  active={colorMode === 'auto'} as="button" type="button"
                  onClick={() => setColorMode('auto')}
                  className="d-flex align-items-center"
                >
                  <CIcon className="me-2" icon={cilContrast} size="lg" /> Auto
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>

            {/* User dropdown */}
            <CDropdown variant="nav-item" alignment="end">
              <CDropdownToggle className="d-flex align-items-center gap-2" caret={false}>
                <span className="d-none d-md-inline">{auth.user?.email ?? ''}</span>
                <CIcon icon={cilUser} size="lg" />
              </CDropdownToggle>
              <CDropdownMenu className="pt-0" style={{ minWidth: 220 }}>
                <CDropdownHeader className="bg-body-secondary fw-semibold">
                  {auth.user?.email ?? 'Signed in'}
                </CDropdownHeader>
                <CDropdownItem disabled className="small text-body-secondary">
                  <CBadge color={ROLE_BADGE_COLOR[auth.user?.role ?? ''] ?? 'secondary'}>
                    {auth.user?.role}
                  </CBadge>
                </CDropdownItem>
                <CDropdownDivider />
                <CDropdownItem as="button" onClick={onLogout}>
                  <CIcon icon={cilAccountLogout} className="me-2" /> Logout
                </CDropdownItem>
                <CDropdownItem as="button" onClick={onLogoutAll}>
                  <CIcon icon={cilAccountLogout} className="me-2" /> Log out on all devices
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
          </CHeaderNav>
        </CHeader>

        {updateAvailable && (
          <div
            className="d-flex align-items-center justify-content-center gap-3 px-3 py-2 text-white"
            style={{ background: 'var(--cui-primary)' }}
          >
            <span className="small">A new version is available.</span>
            <CButton color="light" size="sm" onClick={reload}>
              Refresh Now
            </CButton>
          </div>
        )}

        <CContainer fluid className="px-4 py-4 flex-grow-1">
          {children}
        </CContainer>

        <CFooter className="px-4">
          <div>
            <span className="text-body-secondary small">Costa Rica 2027 · Trip Planner</span>
          </div>
          {version && (
            <div className="ms-auto">
              <span className="text-body-secondary small">{version}</span>
            </div>
          )}
        </CFooter>
      </div>
    </>
  )
}

const NavItemLink: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <CNavItem>
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `nav-link${isActive ? ' active' : ''}`
      }
    >
      {label}
    </NavLink>
  </CNavItem>
)

export default Layout
